import { noop } from 'lodash-es';
import { createContext, useCallback, useContext, useRef } from 'react';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { REACT_APP_NAME } from 'src/utils/constants/constants';
import { queryWithTimeout } from 'src/utils/queryUtils';
import { ClientLogRequest, Feature, LogLevel } from 'src/WebApiController';

import { useClientLogContext } from '../ClientLogContext';
import {
  DefaultComponentTrackingConfig,
  DefaultEndpointTrackingConfig,
} from './AppPerformanceContext.constants';
import { useReportWebVitals } from './AppPerformanceContext.hooks';
import {
  CheckExcessiveCallsOptions,
  CheckExcessiveType,
  IAppPerformanceContext,
  TrackingData,
} from './AppPerformanceContext.types';

export const AppPerformanceContext = createContext<IAppPerformanceContext>({
  trackEndpoint: async () => {
    throw new Error('AppPerformanceContext not initialized');
  },
  trackComponent: () => noop(),
});

export const AppPerformanceContextProvider = ({
  children,
}: React.PropsWithChildren) => {
  const hasFrontendTelemetryContextFeature = useUserHasFeature(
    Feature.FrontendTelemetryContext
  );
  const { logEvent } = useClientLogContext();

  useReportWebVitals(hasFrontendTelemetryContextFeature);

  // Track endpoint call counts using sliding window
  const endpointTrackerRef = useRef<Map<string, TrackingData>>(new Map());
  // Track component render counts using sliding window
  const renderTrackerRef = useRef<Map<string, TrackingData>>(new Map());

  const tryLogExcessiveRequests = useCallback(
    (
      type: CheckExcessiveType,
      id: string,
      tracker: Map<string, TrackingData>,
      options: CheckExcessiveCallsOptions
    ): boolean => {
      const {
        threshold,
        windowMs,
        cooldownMs,
        errorIdPrefix,
        errorMessageTemplate,
      } = options;

      const now = Date.now();
      const existing = tracker.get(id);

      if (!existing) {
        // First call/render
        tracker.set(id, {
          callTimestamps: [now],
          lastLoggedAt: 0,
          totalCalls: 1,
        });
        return true;
      }

      // Add current timestamp
      existing.callTimestamps.push(now);
      existing.totalCalls += 1;

      // Remove timestamps outside the sliding window
      const windowStart = now - windowMs;
      existing.callTimestamps = existing.callTimestamps.filter(
        (timestamp) => timestamp >= windowStart
      );

      // Check if we've exceeded the threshold within the sliding window
      const callsInWindow = existing.callTimestamps.length;
      if (callsInWindow >= threshold) {
        // Only log if we haven't logged recently (cooldown period)
        if (now - existing.lastLoggedAt > cooldownMs) {
          const oldestInWindow = existing.callTimestamps[0];
          const timeSpanMs = now - oldestInWindow;
          const averageIntervalMs =
            timeSpanMs / (callsInWindow - 1) || timeSpanMs;

          const logRequest: ClientLogRequest = {
            logLevel: LogLevel.Warning,
            applicationSource: REACT_APP_NAME,
            errorId: `${errorIdPrefix}-${id}`,
            error: errorMessageTemplate(id, callsInWindow, windowMs),
            pageUrl: window.location.href,
            componentStack: type,
            timestamp: new Date().toISOString(),
            additionalInfo: {
              trackingId: id,
              windowRequestCount: callsInWindow,
              totalRequestCount: existing.totalCalls,
              windowSizeMs: windowMs,
              actualTimeSpanMs: timeSpanMs,
              averageIntervalMs: averageIntervalMs,
              requestPerSecond: (callsInWindow / timeSpanMs) * 1000,
            },
          };

          logEvent(logRequest);
          existing.lastLoggedAt = now;
        }
      }

      // Cleanup stale entries: remove items that haven't been called/rendered
      // beyond the cooldown period to prevent memory leaks
      const staleThreshold = now - cooldownMs;
      for (const [key, value] of tracker.entries()) {
        // Get the most recent timestamp for this entry
        const mostRecentTimestamp =
          value.callTimestamps[value.callTimestamps.length - 1];

        // If the most recent activity is older than cooldown, remove it
        if (mostRecentTimestamp < staleThreshold) {
          tracker.delete(key);
        }
      }

      return true;
    },
    [logEvent]
  );

  const trackEndpoint = useCallback(
    async <T,>(
      endpointName: string,
      promiseFn: Promise<T>,
      onError: (e: Error) => void,
      timeoutMs?: number,
      config = DefaultEndpointTrackingConfig
    ): Promise<T> => {
      // If feature is disabled, execute promise immediately without tracking
      if (!hasFrontendTelemetryContextFeature) {
        return await queryWithTimeout(promiseFn, onError, timeoutMs);
      }

      // Track endpoint calls using helper
      tryLogExcessiveRequests(
        CheckExcessiveType.Endpoint,
        endpointName,
        endpointTrackerRef.current,
        {
          ...config,
          errorIdPrefix: 'ExcessiveEndpointCalls',
          errorMessageTemplate: (name, count, windowMs) =>
            `Endpoint "${name}" has been called ${count} times within ${windowMs}ms`,
          logEvent,
        }
      );

      try {
        return await queryWithTimeout(promiseFn, onError, timeoutMs);
      } catch (error) {
        const lastError = error as Error;

        // Log the failure event
        const logRequest: ClientLogRequest = {
          logLevel: LogLevel.Error,
          applicationSource: REACT_APP_NAME,
          errorId: `TrackEndpoint-${endpointName}`,
          error: `Endpoint ${endpointName} failed`,
          pageUrl: window.location.href,
          componentStack: null,
          timestamp: new Date().toISOString(),
          additionalInfo: {
            endpointName,
            timeoutMs,
            errorMessage: lastError?.message,
            errorStack: lastError?.stack,
          },
        };

        logEvent(logRequest);

        throw lastError;
      }
    },
    [hasFrontendTelemetryContextFeature, logEvent, tryLogExcessiveRequests]
  );

  const trackComponent = useCallback(
    (componentName: string, config = DefaultComponentTrackingConfig) => {
      if (!hasFrontendTelemetryContextFeature) {
        return;
      }

      // Track component renders using helper
      tryLogExcessiveRequests(
        CheckExcessiveType.Component,
        componentName,
        renderTrackerRef.current,
        {
          ...config,
          errorIdPrefix: 'ExcessiveRenders',
          errorMessageTemplate: (name, count, windowMs) =>
            `Component "${name}" has re-rendered ${count} times within ${windowMs}ms`,
          logEvent,
        }
      );
    },
    [hasFrontendTelemetryContextFeature, logEvent, tryLogExcessiveRequests]
  );

  return (
    <AppPerformanceContext.Provider
      value={{
        trackEndpoint,
        trackComponent,
      }}
    >
      {children}
    </AppPerformanceContext.Provider>
  );
};

/**
 * Hook to access performance tracking functions.
 *
 * @returns Object containing trackEndpoint and trackComponent functions
 * @throws Error if used outside of AppPerformanceContextProvider
 *
 * @example
 * function MyComponent() {
 *   const { trackComponent, trackEndpoint } = useAppPerformanceContext();
 *
 *   useEffect(() => {
 *     trackComponent('MyComponent');
 *   });
 *
 *   const loadData = async () => {
 *     return await trackEndpoint('LoadData', {
 *       promiseFn: () => fetchData(),
 *       onError: console.error,
 *     });
 *   };
 * }
 */
export const useAppPerformanceContext = (): IAppPerformanceContext => {
  const context = useContext(AppPerformanceContext);
  if (context === undefined) {
    throw new Error(
      'useAppPerformanceContext must be used within a AppPerformanceContext'
    );
  }
  return context;
};
